/**
 * In-app notifications, delivery preferences, and push device registration.
 *
 * The previous implementation polled `notifications/all-user-notifications`
 * on a timer from a component, compared array lengths to decide whether
 * anything was new, and signed the user out on a 403. All three are gone: the
 * list is a normal cache entry, the unread count comes from the API rather than
 * being counted client-side, and a 401 is handled once in the transport.
 *
 * Push registration is here for completeness of the contract. The web client
 * does not register a device: it has no push credential of its own, and this is
 * the endpoint the mobile apps call.
 */

import { api, LIST_ID } from "../api";
import type {
    Meta,
    Notification,
    NotificationList,
    NotificationPreferences,
    PushDevice,
    RegisterDeviceRequest,
    UpdatePreferencesRequest,
    UUID,
} from "../types";
import type { EnvelopeMeta } from "../baseQuery";

export interface NotificationArgs {
    page?: number;
    limit?: number;
    /** Omit for everything; true or false to narrow to read or unread. */
    read?: boolean;
}

export interface NotificationPage extends NotificationList {
    meta?: Meta;
}

export const notificationsApi = api.injectEndpoints({
    endpoints: (build) => ({
        notifications: build.query<NotificationPage, NotificationArgs | void>({
            query: (args) => ({ url: "notifications", params: args ?? {} }),
            transformResponse: (
                result: NotificationList | null,
                meta: EnvelopeMeta
            ) => ({
                notifications: result?.notifications ?? [],
                unreadCount: result?.unreadCount ?? 0,
                meta: meta?.envelope?.meta,
            }),
            providesTags: (result) => [
                { type: "Notification" as const, id: LIST_ID },
                ...(result?.notifications ?? []).map((n: Notification) => ({
                    type: "Notification" as const,
                    id: n.id,
                })),
            ],
        }),

        markNotificationRead: build.mutation<null, UUID>({
            query: (id) => ({ url: `notifications/${id}/read`, method: "PUT" }),
            invalidatesTags: (_r, _e, id) => [
                { type: "Notification", id },
                { type: "Notification", id: LIST_ID },
            ],
        }),

        markAllNotificationsRead: build.mutation<{ updated: number }, void>({
            query: () => ({ url: "notifications/read-all", method: "PUT" }),
            invalidatesTags: [{ type: "Notification", id: LIST_ID }],
        }),

        deleteNotification: build.mutation<null, UUID>({
            query: (id) => ({ url: `notifications/${id}`, method: "DELETE" }),
            invalidatesTags: [{ type: "Notification", id: LIST_ID }],
        }),

        notificationPreferences: build.query<NotificationPreferences, void>({
            query: () => "notifications/preferences",
            providesTags: ["NotificationPreferences"],
        }),

        updateNotificationPreferences: build.mutation<
            NotificationPreferences,
            UpdatePreferencesRequest
        >({
            query: (body) => ({
                url: "notifications/preferences",
                method: "PUT",
                body,
            }),
            invalidatesTags: ["NotificationPreferences"],
        }),

        pushDevices: build.query<PushDevice[], void>({
            query: () => "notifications/devices",
            transformResponse: (rows: PushDevice[] | null) => rows ?? [],
            providesTags: ["PushDevice"],
        }),

        registerPushDevice: build.mutation<PushDevice, RegisterDeviceRequest>({
            query: (body) => ({
                url: "notifications/devices",
                method: "POST",
                body,
            }),
            invalidatesTags: ["PushDevice"],
        }),

        revokePushDevice: build.mutation<null, string>({
            query: (playerId) => ({
                url: `notifications/devices/${playerId}`,
                method: "DELETE",
            }),
            invalidatesTags: ["PushDevice"],
        }),
    }),
});

export const {
    useNotificationsQuery,
    useMarkNotificationReadMutation,
    useMarkAllNotificationsReadMutation,
    useDeleteNotificationMutation,
    useNotificationPreferencesQuery,
    useUpdateNotificationPreferencesMutation,
    usePushDevicesQuery,
    useRegisterPushDeviceMutation,
    useRevokePushDeviceMutation,
} = notificationsApi;
